// Phase 1.5 item #2B · Prompts pages
//
// Backend routes:
//   GET    /api/observability/prompts
//   GET    /api/observability/prompts/{name}
//   GET    /api/observability/prompts/{name}/versions
//   POST   /api/observability/prompts/{name}/versions          (admin)
//   POST   /api/observability/prompts/{name}/promote           (OWNER only — RBAC §6)
//
// Promote = label a specific version `production` so the next chat
// turn uses it. Hot-reload <30s per design §5 invariant #6.
import React, { useEffect, useState } from "react";
import { COLORS } from "../../../theme/colors";
import { Button, Modal, TextArea, Badge } from "../../../components/ui";
import { observabilityApi } from "../../../api/client";
import { useAuth } from "../../../contexts/AuthContext";
import { MarkdownText } from "../../../components/MarkdownText";
import { EmptyState, ErrorState, LoadingRow, TableHeader, TableRow } from "./SharedUI";
import { humanizeError as _humanizeError } from "../../../utils/humanizeError";

// MNT-014: shared humanizer + this page's status-specific overrides.
const humanizeError = (e) => _humanizeError(e, {
  500: "Langfuse query failed.",
  403: "Promote requires owner role.",
  404: "Prompt not found.",
});

const OWNER_ROLES = ["owner"];
const ADMIN_ROLES = ["owner", "administrator", "developer"];

// FCOV: pure role-gate helpers extracted for unit testing; wired back below.
// Promote-to-production is OWNER-only (RBAC §6).
export function canPromotePrompt(role) {
  return OWNER_ROLES.includes(role || "");
}
// Creating a new prompt version is allowed for owner/administrator/developer.
export function canCreatePromptVersion(role) {
  return ADMIN_ROLES.includes(role || "");
}
// Normalize the various shapes listPrompts / listPromptVersions can return
// (bare array, {data:[...]}, or a wrapping object) into a plain array.
export function normalizePromptItems(res) {
  const list = res?.data || res || [];
  return Array.isArray(list) ? list : (list?.data || []);
}

export function PromptsListPage({ navigate }) {
  const [prompts, setPrompts] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const res = await observabilityApi.listPrompts();
      if (res?.success === false) setError(res.error || "Failed to load");
      else setPrompts(res?.data || res || []);
    } catch (e) {
      setError(humanizeError(e));
      setPrompts([]);
    }
  };
  useEffect(() => { load(); }, []);

  if (prompts === null && !error) return <LoadingRow label="Loading prompts…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  const items = Array.isArray(prompts) ? prompts : (prompts?.data || []);

  return (
    <div>
      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 12 }}>
        {items.length} prompt{items.length === 1 ? "" : "s"} · production label hot-reloads in &lt; 30s
      </div>

      {items.length === 0 ? (
        <EmptyState
          title="No prompts versioned yet"
          detail="Prompts get auto-mirrored to Langfuse when agents emit them. Send a chat to populate."
        />
      ) : (
        <div>
          <TableHeader cols={["NAME", "PRODUCTION", "LATEST", "UPDATED"]} />
          {items.map(p => (
            <TableRow
              key={p.name}
              onClick={() => navigate(`/analytics/observability/prompts/${encodeURIComponent(p.name)}`)}
              cells={[
                <strong key="n" style={{ fontFamily: "monospace", fontSize: 12 }}>{p.name}</strong>,
                <Badge key="p" color={COLORS.green}>
                  v{p.production_version ?? p.version ?? "—"}
                </Badge>,
                <span key="l" style={{ fontSize: 11, color: COLORS.textMuted }}>
                  v{p.latest_version ?? p.version ?? "—"}
                </span>,
                <span key="u" style={{ fontSize: 11, color: COLORS.textMuted }}>
                  {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "—"}
                </span>,
              ]}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function PromptVersionsPage({ detailId, navigate }) {
  const name = decodeURIComponent(detailId || "");
  const { currentUser } = useAuth();
  const canPromote = canPromotePrompt(currentUser?.role);
  const canCreate = canCreatePromptVersion(currentUser?.role);

  const [versions, setVersions] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newContent, setNewContent] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const res = await observabilityApi.listPromptVersions(name);
      if (res?.success === false) setError(res.error || "Failed to load");
      else {
        const items = normalizePromptItems(res);
        setVersions(items);
        if (items.length && !selected) setSelected(items[0]);
      }
    } catch (e) {
      setError(humanizeError(e));
      setVersions([]);
    }
  };
  useEffect(() => { load(); }, [name]);

  const onPromote = async (version) => {
    if (!confirm(`Promote v${version} to production? This affects all chats using "${name}".`)) return;
    setBusy(true);
    try {
      const res = await observabilityApi.promotePrompt(name, version, "production");
      if (res?.success === false) setError(res.error || "Promote failed");
      else await load();
    } catch (e) { setError(humanizeError(e)); }
    finally { setBusy(false); }
  };

  const onCreate = async () => {
    if (!newContent.trim()) return;
    setBusy(true);
    try {
      const res = await observabilityApi.createPromptVersion(name, newContent, []);
      if (res?.success === false) setError(res.error || "Create failed");
      else { setCreating(false); setNewContent(""); await load(); }
    } catch (e) { setError(humanizeError(e)); }
    finally { setBusy(false); }
  };

  if (versions === null && !error) return <LoadingRow label="Loading versions…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <button
          onClick={() => navigate("/analytics/observability/prompts")}
          style={{
            background: "none", border: "none",
            color: COLORS.accentText || COLORS.accent || "#2563eb",
            cursor: "pointer", fontSize: 12, fontWeight: 600,
          }}
        >‹ Prompts</button>
        <span style={{ color: COLORS.textMuted }}>|</span>
        <div style={{ fontSize: 13, color: COLORS.text, fontWeight: 700, fontFamily: "monospace" }}>
          {name}
        </div>
        <div style={{ marginLeft: "auto" }}>
          {canCreate && (
            <Button onClick={() => setCreating(true)} size="sm">+ New version</Button>
          )}
        </div>
      </div>

      {/* Two-column: versions on left, content viewer on right */}
      <div style={{
        display: "grid", gridTemplateColumns: "260px 1fr",
        gap: 16, minHeight: 400,
      }}>
        <div style={{
          border: `1px solid ${COLORS.border}`, borderRadius: 6,
          background: COLORS.surface, padding: 6, overflowY: "auto",
        }}>
          {(versions || []).map(v => {
            const isSel = selected?.version === v.version;
            const isProd = (v.labels || []).includes("production");
            return (
              <div
                key={v.version}
                onClick={() => setSelected(v)}
                style={{
                  padding: "10px 12px", borderRadius: 4, cursor: "pointer",
                  background: isSel ? (COLORS.accentSoft || COLORS.surfaceHover) : "transparent",
                  marginBottom: 4,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <strong style={{ fontSize: 12, color: COLORS.text }}>v{v.version}</strong>
                  {isProd && <Badge color={COLORS.green}>production</Badge>}
                </div>
                <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>
                  {v.created_at ? new Date(v.created_at).toLocaleString() : "—"}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{
          border: `1px solid ${COLORS.border}`, borderRadius: 6,
          background: COLORS.surface, padding: 14, overflowY: "auto",
          minHeight: 0,
        }}>
          {!selected ? (
            <div style={{ color: COLORS.textMuted, fontSize: 12, padding: 20 }}>
              Select a version to view its content.
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8,
                            marginBottom: 10, paddingBottom: 8,
                            borderBottom: `1px solid ${COLORS.border}` }}>
                <strong style={{ fontSize: 13 }}>v{selected.version}</strong>
                {(selected.labels || []).map(l => (
                  <Badge key={l} color={l === "production" ? COLORS.green : COLORS.textMuted}>{l}</Badge>
                ))}
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  {canPromote && !(selected.labels || []).includes("production") && (
                    <Button onClick={() => onPromote(selected.version)} size="sm"
                            disabled={busy} loading={busy}>
                      Promote to production
                    </Button>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.5 }}>
                <MarkdownText text={selected.prompt || selected.content || ""} />
              </div>
            </>
          )}
        </div>
      </div>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title={`New version of ${name}`}
        width={760}
        footer={
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="secondary" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={onCreate} disabled={!newContent.trim() || busy} loading={busy}>
              Save version
            </Button>
          </div>
        }
      >
        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8 }}>
          New versions start unlabeled. Promote to production after testing.
        </div>
        <TextArea
          value={newContent}
          onChange={e => setNewContent(e.target.value)}
          placeholder="Paste the new prompt content…"
          rows={16}
          style={{ fontFamily: "monospace", fontSize: 12 }}
        />
      </Modal>
    </div>
  );
}

